import { lazyPopoutMailReadingPaneUtil } from "owa-mail-popout-reading-pane/lib/lazyFunctions";
import { popoutProjectionMountedPromise } from "owa-popout-v2/lib/store/parentStore";
import { convertRestIdToEwsId } from "owa-identifiers";
import { resolveMailboxFromRouteKey } from "owa-mailbox-info/lib/resolveMailboxFromRouteKey";
import { getMailboxInfoFromDeeplinkParams } from "owa-mailbox-info/lib/getMailboxInfoFromDeeplinkParams";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { logCoreUsage } from "owa-analytics/lib/api/logCoreUsage";
import { logUsage } from "owa-analytics";
import isApplinkRoute from "owa-url/lib/isApplinkRoute";
import { getApplicationSettings } from "owa-application-settings";
import { getApplinkAccountRedirectContext, tryRedirectForSharedOrUnresolvedAccount, } from "./tryRedirectForSharedOrUnresolvedAccount";

const PROJECTION_MOUNT_TIMEOUT_MS = 30000;

export interface MailReadRouteParameters {
    itemId?: string;
    routeKey?: string;
    domain?: string;
    account?: string;
}

export default async function mailReadRouteHandler(parameters: MailReadRouteParameters): Promise<void> {
    // Capture initial context immediately before any operations may alter them, such as redirects
    const { itemId, routeKey, domain, account } = parameters;
    const applinkAccountRedirectContext = getApplinkAccountRedirectContext(routeKey);
    const isApplink = isApplinkRoute();
    
    if (!itemId) {
        logCoreUsage("MailReadRouteHandlerNoItemId", getGlobalSettingsAccountMailboxInfo());
        return;
    }
    
    // Resolve the target mailbox: prefer Universal Link route key (when flighted),
    // fall back to deeplink params
    const mailboxInfo = routeKey
        ? await resolveMailboxFromRouteKey(routeKey, "ReadFromApplink")
        : getMailboxInfoFromDeeplinkParams(account, domain, undefined /*groupAlias*/, undefined /*groupDomain*/, getGlobalSettingsAccountMailboxInfo());
    
    if (isApplink) {
        logCoreUsage("ApplinkMailReadRouteHandlerInitiated", mailboxInfo, {
            platform: getApplicationSettings("PlatformType").isMonarch ? "Monarch" : "OWA",
        });
    }
    
    if (!mailboxInfo &&
        (await tryRedirectForSharedOrUnresolvedAccount(applinkAccountRedirectContext))) {
        return;
    }
    
    // Wait for the projection host to mount. Log if it takes longer than expected,
    // but continue waiting — the app may still be booting on cold start.
    const projectionMountTimer = setTimeout(() => {
        logUsage("PopoutProjectionMountDelayed", mailboxInfo, {
            waitTimeMs: PROJECTION_MOUNT_TIMEOUT_MS,
        });
    }, PROJECTION_MOUNT_TIMEOUT_MS);
    
    await popoutProjectionMountedPromise;
    clearTimeout(projectionMountTimer);
    
    // Decode the encoded item id from the URL
    const decodedEwsId = convertRestIdToEwsId(decodeURIComponent(itemId));
    
    // Call the popout mail reading pane util directly to ensure packages are loaded
    await lazyPopoutMailReadingPaneUtil.importAndExecute(decodedEwsId, undefined /*conversationId*/, undefined /*internetMessageId*/, mailboxInfo, undefined /*data*/, undefined /*freezeDryId*/, undefined /*isFileActivation*/, undefined /*fileActivationExt*/, undefined /*isCachedItem*/, undefined /*eventTimestamp*/, undefined /*popOutRowKey*/, undefined /*openBizChatParams*/, undefined /*clientMessage*/, isApplink);
}
